import React, { useCallback, useMemo } from "react";
import moment from "moment-timezone";
import range from "lodash.range";

import { captureException } from "services/captureException";
import { getBooleanOptions } from "services/getBooleanOptions";
import { OutputRow as ExternalCommitmentsData } from "api/getExternalCommitments";
import { ValidationCallback } from "validation/validate";
import ModalFieldSelect from "components/ModalFieldSelect";
import ModalFieldHourOfDay from "components/ModalFieldHourOfDay";
import { OnCompleteOutput } from "components/ModalWizard/ModalWizard";
import ModalFieldDate from "components/ModalFieldDate";
import ModalFieldText from "components/ModalFieldText";
import {
	Selection,
	CompleteSelection,
	getEmptySelection,
	isComplete,
	isEmpty
} from "components/FieldDate/FieldDateSelection";
import EditableTable from "components/EditableTable";
import { OnEdit } from "components/EditableTable/EditableTable";
import { columnDefinitionTimezone } from "components/EditableTable/columnDefinitionTimezone";
import { useStickySort } from "hooks/useStickySort";
import StepTimeFrom from "./NewRowWizard/StepTimeFrom";
import StepTimeTo from "./NewRowWizard/StepTimeTo";
import StepDateFrom from "./NewRowWizard/StepDateFrom";
import StepDateTo from "./NewRowWizard/StepDateTo";
import StepTimezone from "components/PageDashboard/StepTimezone";
import StepDescription from "./NewRowWizard/StepDescription";
import StepIsExternallyBookedMeeting from "./NewRowWizard/StepIsExternallyBookedMeeting";
import terminology from "terminology.json";
import { formatAsTime } from "utils/datesAndTimes/formatAsTime";
import { withNewValue } from "services/withNewValue";
import { convertToDurationAndAddToObject } from "services/convertToDurationAndAddToObject";
import { useHandleOKBooleanField } from "hooks/useHandleOKBooleanField";
import { useAddActionButtonDelete } from "hooks/useAddActionButtonDelete";

export interface DataInAnyRow
	extends Omit<
		ExternalCommitmentsData,
		"dateStartsUTC" | "dateEndsJustBeforeUTC"
	> {
	dateStartsInRowTimezone: CompleteSelection;
	dateEndsJustBeforeInRowTimezone: CompleteSelection;
	timeStartsInRowTimezone: moment.Duration;
	timeEndsJustBeforeInRowTimezone: moment.Duration;
}

interface ExternalCommitmentsTableProps {
	commitments: DataInAnyRow[];
	isLoading: boolean;
	isWaiting: boolean;
	isError: boolean;
	waitModalTitle: string;
	waitModalIsShowing: boolean;
	onEdit: (
		rows: {
			old?: DataInAnyRow;
			new?: DataInAnyRow;
		}[]
	) => OnCompleteOutput;
}

const ExternalCommitmentsTable: React.FunctionComponent<
	ExternalCommitmentsTableProps
> = ({
	commitments,
	onEdit: onEditGlobal,
	isLoading,
	isWaiting,
	isError,
	waitModalTitle,
	waitModalIsShowing
}) => {
	const onNewRowWizardComplete = useCallback(
		(data: DataInAnyRow) => {
			return onEditGlobal([
				{
					new: {
						...data
					}
				}
			]);
		},
		[onEditGlobal]
	);

	const sortedExternalCommitmentsPeriods = useStickySort(
		commitments,
		useCallback((a: DataInAnyRow, b: DataInAnyRow) => {
			const aStart = moment(a.dateStartsInRowTimezone)
				.toDate()
				.getTime();
			const bStart = moment(b.dateStartsInRowTimezone)
				.toDate()
				.getTime();

			if (aStart !== bStart) {
				return aStart - bStart;
			}

			if (a.timeStartsInRowTimezone !== b.timeStartsInRowTimezone) {
				return (
					a.timeStartsInRowTimezone.as("minutes") -
					b.timeStartsInRowTimezone.as("minutes")
				);
			}

			const aEnd = moment(a.dateEndsJustBeforeInRowTimezone)
				.toDate()
				.getTime();
			const bEnd = moment(b.dateEndsJustBeforeInRowTimezone)
				.toDate()
				.getTime();

			if (aEnd !== bEnd) {
				return aEnd - bEnd;
			}

			if (
				a.timeEndsJustBeforeInRowTimezone !== b.timeEndsJustBeforeInRowTimezone
			) {
				return (
					a.timeEndsJustBeforeInRowTimezone.as("minutes") -
					b.timeEndsJustBeforeInRowTimezone.as("minutes")
				);
			}

			return 0;
		}, [])
	);

	const rows = useAddActionButtonDelete(
		sortedExternalCommitmentsPeriods,
		onEditGlobal,
		data => data.source !== "manual"
	);

	const handleOKHourOfDayField = useCallback(function addTime(
		row: DataInAnyRow,
		newValue: number,
		rowKey: keyof DataInAnyRow,
		onEdit: OnEdit<DataInAnyRow>
	) {
		return withNewValue(newValue, () => {
			const newData = convertToDurationAndAddToObject(row, newValue, rowKey);
			onEdit([{ old: row, new: newData }]);
		});
	},
	[]);

	const handleOKBooleanField = useHandleOKBooleanField();

	const newRowWizard = useMemo(() => {
		const steps = [
			{
				key: "timezone",
				title: "What timezone will you set the times in?",
				shortTitle: "Time zone",
				component: StepTimezone,
				doScrollToInitialPosition: true
			},
			{
				key: "date-from",
				title: "What date does the commitment start on?",
				shortTitle: "Date from",
				component: StepDateFrom
			},
			{
				key: "time-from",
				title: "What time does the commitment start at?",
				shortTitle: "Time from",
				component: StepTimeFrom
			},
			{
				key: "date-to",
				title: "What date does the commitment end on?",
				shortTitle: "Date to",
				component: StepDateTo
			},
			{
				key: "time-to",
				title: "What time does the commitment end at?",
				shortTitle: "Time to",
				component: StepTimeTo
			},
			{
				key: "description",
				title: "What will you be doing?  (this is private to you)",
				shortTitle: "Description",
				component: StepDescription
			},
			{
				key: "isExternallyBookedMeeting",
				title: `Is this a teaching appointment?`,
				shortTitle: "Type",
				component: StepIsExternallyBookedMeeting
			}
		];

		const validateNewRowData: ValidationCallback<DataInAnyRow> = (
			input,
			{ flag, checkField }
		) => {
			const {
				timeStartsInRowTimezone,
				timeEndsJustBeforeInRowTimezone
			} = input;

			checkField("dateStartsInRowTimezone", {
				type: "completedatefieldselection"
			});
			checkField("dateEndsJustBeforeInRowTimezone", {
				type: "completedatefieldselection",
				isRequired: false
			});
			checkField("timezone", { type: "string" });

			if (!timeStartsInRowTimezone) {
				flag("timeStartsInRowTimezone", "mising");
			} else if (!moment.isDuration(timeStartsInRowTimezone)) {
				flag("timeStartsInRowTimezone", "not a moment duration");
			}

			if (!timeEndsJustBeforeInRowTimezone) {
				flag("timeEndsJustBeforeInRowTimezone", "mising");
			} else if (!moment.isDuration(timeEndsJustBeforeInRowTimezone)) {
				flag("timeEndsJustBeforeInRowTimezone", "not a moment duration");
			}

			checkField("isExternallyBookedMeeting", { type: "boolean" });
		};

		const initialData = {};

		return {
			steps,
			validateNewRowData,
			initialData
		};
	}, []);

	return (
		<EditableTable<DataInAnyRow>
			allowDeleteRows
			onEdit={onEditGlobal}
			dataDescriptionSingular="external commitment"
			dataDescriptionPlural="external commitments"
			explanation={`You can use this section to record periods when you will not be available for ${terminology.siteName} ${terminology.meetings}.`}
			onNew={onNewRowWizardComplete}
			{...{
				isLoading,
				isWaiting,
				isError,
				waitModalTitle,
				waitModalIsShowing,
				rows,
				newRowWizard
			}}
			cols={[
				columnDefinitionTimezone({ initialValue: row => row.timezone }),
				{
					key: "dateStartsInRowTimezone",
					heading: "Start date",
					field: ({ row, rowKey, isEditing, onEdit }) => (
						<ModalFieldDate
							isUnlocked={isEditing}
							formatSelection={selection =>
								formatSelection(selection, isEditing)
							}
							title="Valid from"
							requireSelectedDate
							onOK={newDate => {
								if (!newDate) {
									throw captureException(new Error("No selected date"), {
										evtType: "noSelectedDate"
									});
								}

								if (isEmpty(newDate)) {
									throw captureException(new Error("Selected date was empty"), {
										evtType: "selectedDateWasEmpty"
									});
								}

								const newData = {
									...row,
									[rowKey]: newDate
								};

								onEdit([{ old: row, new: newData }]);
							}}
							allowEmptyValue={false}
							value={row.dateStartsInRowTimezone}
							mustBeBefore={row.dateEndsJustBeforeInRowTimezone}
						/>
					)
				},
				{
					key: "timeStartsInRowTimezone",
					heading: "Start time",
					field: ({ row, rowKey, isEditing, onEdit, isReadOnly }) =>
						isReadOnly ? (
							/* Show text rather than the hour-of-day field in read-only mode, because imported commitments may not start and end on the hour */ formatAsTime(
								row.timeStartsInRowTimezone
							)
						) : (
							<ModalFieldHourOfDay
								isUnlocked={isEditing}
								title="Available from"
								value={row.timeStartsInRowTimezone.hours()}
								requireSelectedTime
								isDisabled={(value: number | undefined) => {
									if (row.dateStartsInRowTimezone === undefined) {
										return false;
									}

									if (value === undefined) {
										return false;
									}

									if (row.dateEndsJustBeforeInRowTimezone === undefined) {
										return false;
									}

									if (row.timeEndsJustBeforeInRowTimezone === undefined) {
										return false;
									}

									if (row.timezone === undefined) {
										return false;
									}

									const startDateTime = moment
										.tz(row.dateStartsInRowTimezone, row.timezone)
										.hours(value)
										.startOf("hour");
									const endDateTime = moment
										.tz(row.dateEndsJustBeforeInRowTimezone, row.timezone)
										.hours(row.timeEndsJustBeforeInRowTimezone.hours())
										.startOf("hour");

									return startDateTime.isSameOrAfter(endDateTime);
								}}
								onOK={(newValue: number | undefined) => {
									withNewValue(newValue, value => {
										handleOKHourOfDayField(row, value, rowKey, onEdit);
									});
								}}
							/>
						)
				},
				{
					key: "dateEndsJustBeforeInRowTimezone",
					heading: "End date",
					field: ({ row, rowKey, isEditing, onEdit }) => (
						<ModalFieldDate
							isUnlocked={isEditing}
							formatSelection={selection =>
								formatSelection(selection, isEditing)
							}
							title="Valid until"
							onOK={newDate => {
								const newData = {
									...row,
									[rowKey]: newDate && isComplete(newDate) ? newDate : undefined
								};

								onEdit([{ old: row, new: newData }]);
							}}
							allowEmptyValue={true}
							value={
								row.dateEndsJustBeforeInRowTimezone
									? row.dateEndsJustBeforeInRowTimezone
									: getEmptySelection()
							}
							mustBeAfter={row.dateStartsInRowTimezone}
						/>
					)
				},
				{
					key: "timeEndsJustBeforeInRowTimezone",
					heading: "End time",
					field: ({ row, rowKey, isEditing, onEdit, isReadOnly }) =>
						isReadOnly ? (
							/* Show text rather than the hour-of-day field in read-only mode, because imported commitments may not start and end on the hour */ formatAsTime(
								row.timeEndsJustBeforeInRowTimezone
							)
						) : (
							<ModalFieldHourOfDay
								isUnlocked={isEditing}
								title="Available until"
								value={row.timeEndsJustBeforeInRowTimezone.hours()}
								requireSelectedTime
								isDisabled={(value: number | undefined) => {
									if (row.dateStartsInRowTimezone === undefined) {
										return false;
									}

									if (row.timeStartsInRowTimezone === undefined) {
										return false;
									}

									if (row.dateEndsJustBeforeInRowTimezone === undefined) {
										return false;
									}

									if (value === undefined) {
										return false;
									}

									if (row.timezone === undefined) {
										return false;
									}

									const startDateTime = moment
										.tz(row.dateStartsInRowTimezone, row.timezone)
										.hours(row.timeStartsInRowTimezone.hours())
										.startOf("hour");
									const endDateTime = moment
										.tz(row.dateEndsJustBeforeInRowTimezone, row.timezone)
										.hours(value)
										.startOf("hour");

									return endDateTime.isSameOrBefore(startDateTime);
								}}
								onOK={(newValue: number | undefined) => {
									withNewValue(newValue, value => {
										handleOKHourOfDayField(row, value, rowKey, onEdit);
									});
								}}
							/>
						)
				},
				{
					key: "description",
					heading: "Description",
					field: ({ row, rowKey, isEditing, onEdit }) => (
						<ModalFieldText
							isUnlocked={isEditing}
							title="Description"
							helptext="What will you be doing?  (this is private to you)"
							onOK={newValue => {
								const newData = {
									...row,
									[rowKey]: newValue
								};
								onEdit([{ old: row, new: newData }]);
							}}
							value={row.description ? row.description : ""}
						/>
					)
				},
				{
					key: "isExternallyBookedMeeting",
					heading: `Is teaching appointment?`,
					field: ({ row, rowKey, isEditing, onEdit }) => (
						<ModalFieldSelect
							isUnlocked={isEditing}
							title={`Is this a ${terminology.meeting} booked outside this system?`}
							requireSelection
							onOK={newOptions =>
								handleOKBooleanField(row, newOptions, rowKey, onEdit)
							}
							options={getBooleanOptions(!!row.isExternallyBookedMeeting)}
							allowMultipleSelections={false}
							useCheckboxes={true}
						/>
					)
				}
			]}
		/>
	);
};

export default ExternalCommitmentsTable;

export function getDayOfWeekOptions(selectedValues?: number[]) {
	return range(1, 8).map(dayOfWeek => ({
		id: dayOfWeek + "",
		value: dayOfWeek,
		text: formatDayOfWeek(dayOfWeek),
		selected: !!selectedValues && selectedValues.includes(dayOfWeek)
	}));
}

function formatDayOfWeek(dayOfWeek: number) {
	return moment()
		.isoWeekday(dayOfWeek)
		.format("ddd");
}

function formatSelection(
	selection: Selection,
	isShortVersion: boolean = false
) {
	if (!isComplete(selection)) {
		return "";
	}

	const formatString = isShortVersion ? `DD/MMM/YYYY` : `ddd Do MMM YYYY`;
	return moment.utc(selection).format(formatString);
}
